import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import LeaveRequestService from '../../services/leave-request.service';
import EmployeeService from '../../services/employee.service';

const LeaveRequestList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableEmployees, setAvailableEmployees] = useState<any[]>([]);

  useEffect(() => {
    fetchLeaveRequests();
  }, [statusFilter]);

  useEffect(() => {
    fetchAvailableEmployees();
  }, []);

  const fetchAvailableEmployees = async () => {
    try {
      const response = await EmployeeService.getAll({
        'pagination[pageSize]': 10,
        'filters[status][$eq]': 'active',
      });
      setAvailableEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['employee', 'approvedBy'],
        'pagination[pageSize]': 100,
        sort: 'createdAt:desc',
      };

      if (statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }

      const response = await LeaveRequestService.getAll(params);
      setLeaveRequests(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        await LeaveRequestService.delete(id);
        fetchLeaveRequests();
      } catch (error) {
        console.error('Error deleting leave request:', error);
      }
    }
  };

  const handleApprove = async (id: number) => {
    if (window.confirm('Approve this leave request?')) {
      try {
        // Try to get a valid employee ID for approvedBy
        // First, try to use the first available employee
        let approvedByEmployeeId: number | null = null;
        
        if (availableEmployees.length > 0) {
          // Use the first available employee
          approvedByEmployeeId = availableEmployees[0].id;
        } else {
          // If no employees in cache, try to fetch one
          try {
            const response = await EmployeeService.getAll({
              'pagination[pageSize]': 1,
              'filters[status][$eq]': 'active',
            });
            if (response.data?.data?.length > 0) {
              approvedByEmployeeId = response.data.data[0].id;
            }
          } catch (err) {
            console.warn('Could not fetch employee for approvedBy:', err);
          }
        }

        // If we have an employee ID, use it; otherwise, approvedBy will be null (optional field)
        await LeaveRequestService.approve(id, approvedByEmployeeId);
        fetchLeaveRequests();
      } catch (error: any) {
        console.error('Error approving leave request:', error);
        const errorMessage = error.response?.data?.error?.message || 'Failed to approve leave request';
        alert(`Error: ${errorMessage}`);
      }
    }
  };

  const handleReject = async (id: number) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await LeaveRequestService.reject(id, reason);
        fetchLeaveRequests();
      } catch (error) {
        console.error('Error rejecting leave request:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getLeaveTypeColor = (type: string) => {
    switch (type) {
      case 'vacation':
        return 'primary';
      case 'sick':
        return 'error';
      case 'personal':
        return 'info';
      case 'maternity':
      case 'paternity':
        return 'secondary';
      case 'unpaid':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredLeaveRequests = leaveRequests.filter((request) => {
    const employee = request.attributes?.employee?.data?.attributes;
    const searchLower = searchTerm.toLowerCase();
    return (
      employee?.firstName?.toLowerCase().includes(searchLower) ||
      employee?.lastName?.toLowerCase().includes(searchLower) ||
      request.attributes?.leaveType?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Leave Requests</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/leave-requests/create')}
        >
          New Leave Request
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by employee name or leave type"
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Leave Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Days</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Approved By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredLeaveRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No leave requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredLeaveRequests.map((request) => {
                const attrs = request.attributes;
                const employee = attrs?.employee?.data?.attributes;
                const approver = attrs?.approvedBy?.data?.attributes;
                const isPending = attrs?.status === 'pending';

                return (
                  <TableRow key={request.id}>
                    <TableCell>
                      {employee
                        ? `${employee.firstName} ${employee.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.leaveType || '-'}
                        color={getLeaveTypeColor(attrs?.leaveType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(attrs?.startDate)}</TableCell>
                    <TableCell>{formatDate(attrs?.endDate)}</TableCell>
                    <TableCell>
                      <strong>{attrs?.totalDays || '-'}</strong> days
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.status || 'pending'}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {approver
                        ? `${approver.firstName} ${approver.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {isPending && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(request.id)}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReject(request.id)}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/leave-requests/${request.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(request.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LeaveRequestList;

